import React, { createContext, useContext, useState, useEffect } from 'react';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    // Load permissions from localStorage on component mount
    const storedPermissions = localStorage.getItem('userPermissions');
    const storedRole = localStorage.getItem('roleName');
    
    if (storedPermissions) {
      setPermissions(JSON.parse(storedPermissions));
    }
    if (storedRole) {
      setUserRole(storedRole);
    }
  }, []);

  // Helper functions for checking permissions
  const hasPermission = (menuId) => {
    return permissions[menuId]?.hasAccess || false;
  };

  const hasReadAccess = (menuId) => {
    return permissions[menuId]?.readAccess || false;
  };

  const hasWriteAccess = (menuId) => {
    return permissions[menuId]?.writeAccess || false;
  };

  const canPerformAction = (menuId, action) => {
    const menuPermissions = permissions[menuId];
    if (!menuPermissions?.hasAccess) return false;

    switch (action) {
      case 'view':
      case 'read':
        return menuPermissions.readAccess;
      case 'add':
      case 'edit':
      case 'delete':
      case 'write':
        return menuPermissions.writeAccess;
      default:
        return false;
    }
  };

  const getMenuPermission = (menuId) => {
    return permissions[menuId] || {
      menuName: '',
      permission: 'no',
      readAccess: false,
      writeAccess: false,
      hasAccess: false
    };
  };

  const value = {
    permissions,
    userRole,
    hasPermission,
    hasReadAccess,
    hasWriteAccess,
    canPerformAction,
    getMenuPermission,
    setPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};